/**
 * BudgetSelectionBridge — Editor selection state (line ids).
 * Selection is UI concern; Engine remains unaware.
 */

export type SelectionListener = (selectedLineIds: readonly string[]) => void

export class BudgetSelectionBridge {
  private selected = new Set<string>()
  private listeners = new Set<SelectionListener>()

  getSelectedIds(): string[] {
    return [...this.selected]
  }

  isSelected(lineId: string): boolean {
    return this.selected.has(lineId)
  }

  select(lineId: string): void {
    this.selected = new Set([lineId])
    this.emit()
  }

  toggle(lineId: string): void {
    if (this.selected.has(lineId)) this.selected.delete(lineId)
    else this.selected.add(lineId)
    this.emit()
  }

  selectMany(lineIds: string[]): void {
    this.selected = new Set(lineIds)
    this.emit()
  }

  clear(): void {
    if (this.selected.size === 0) return
    this.selected.clear()
    this.emit()
  }

  /** Drop ids that no longer exist after Engine mutations. */
  prune(existingLineIds: Iterable<string>): void {
    const allowed = new Set(existingLineIds)
    let changed = false
    for (const id of [...this.selected]) {
      if (!allowed.has(id)) {
        this.selected.delete(id)
        changed = true
      }
    }
    if (changed) this.emit()
  }

  subscribe(listener: SelectionListener): () => void {
    this.listeners.add(listener)
    listener(this.getSelectedIds())
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(): void {
    const ids = this.getSelectedIds()
    for (const listener of this.listeners) {
      listener(ids)
    }
  }
}
